import type { CatalogProfileDetailDto } from '@/api/catalog-client';
import type {
  BriefingCycleDto,
  SupplierBriefingDto,
} from '@/api/supplier-conditions-client';

// =============================================================================
// supplier-campaigns-model.ts — Sélecteurs PURS de la page « Mes campagnes en cours » (F, GATE 2026-09-11).
// =============================================================================
// La page liste→détail réutilise l'ARBORESCENCE DU CATALOGUE (familles/sous-familles/profils + recherche
// par niveau) restreinte au PÉRIMÈTRE d'une campagne. Le read-model conditions (`SupplierBriefingDto`) ne
// porte PAS la sous-famille ni les dates campagne : ce module en DÉRIVE ce qui manque, SANS toucher l'API
// (décisions de données GATE 3/5).
//
// Fonctions PURES (sans état, sans I/O), colocalisées + testées unitairement :
//   • deriveCampaignPeriod : dates de démarrage/fin DÉRIVÉES des cycles (min openAt / max closeAt) — le
//     read-model n'expose pas de dates campagne (décision GATE 3).
//   • perimeterProfileCodes / briefingDescriptionByCode : projection des profils du périmètre + leur
//     descriptif « attendu » (décision GATE 5 : description au clic = briefing si présente, sinon catalogue).
//   • buildPerimeterProfiles : INTERSECTION de la structure catalogue (hiérarchie complète, sous-familles)
//     avec les codes du périmètre — la hiérarchie vient du catalogue, filtrée aux profils de la campagne.

/** Fenêtre calendaire d'une campagne DÉRIVÉE de ses cycles (décision GATE 3). `null` = aucun jalon planifié. */
export interface CampaignPeriod {
  /** Démarrage = plus petit `openAt` parmi les cycles (ISO 8601), ou `null`. */
  readonly start: string | null;
  /** Fin = plus grand `closeAt` parmi les cycles (ISO 8601), ou `null`. */
  readonly end: string | null;
}

/**
 * Dérive la période de la campagne à partir de ses cycles : démarrage = min(openAt), fin = max(closeAt).
 * Les horodatages ISO 8601 (même suffixe UTC `Z`) se comparent lexicographiquement dans l'ordre chronologique.
 * Décision GATE 3 : le read-model conditions n'expose pas de dates campagne — on les dérive (libellé explicite
 * « Période (d'après les cycles) » côté écran, jamais présenté comme une donnée d'autorité).
 */
export function deriveCampaignPeriod(
  cycles: readonly BriefingCycleDto[],
): CampaignPeriod {
  let start: string | null = null;
  let end: string | null = null;
  for (const cycle of cycles) {
    if (cycle.openAt !== null && (start === null || cycle.openAt < start)) {
      start = cycle.openAt;
    }
    if (cycle.closeAt !== null && (end === null || cycle.closeAt > end)) {
      end = cycle.closeAt;
    }
  }
  return { start, end };
}

/** Codes JeKo des profils du PÉRIMÈTRE de la campagne (décision GATE 5 : filtre appliqué au catalogue). */
export function perimeterProfileCodes(
  briefing: SupplierBriefingDto,
): ReadonlySet<string> {
  const codes = new Set<string>();
  for (const family of briefing.perimetre.families) {
    for (const profile of family.profiles) {
      codes.add(profile.profileCode);
    }
  }
  return codes;
}

/**
 * Descriptif de l'ATTENDU par code JeKo (décision GATE 5) : la description du briefing quand elle est
 * présente (non vide). Absente ⇒ la clé n'est PAS posée ⇒ l'écran retombe sur la description du catalogue.
 */
export function briefingDescriptionByCode(
  briefing: SupplierBriefingDto,
): ReadonlyMap<string, string> {
  const byCode = new Map<string, string>();
  for (const family of briefing.perimetre.families) {
    for (const profile of family.profiles) {
      const description = profile.description?.trim() ?? '';
      if (description.length > 0) {
        byCode.set(profile.profileCode, description);
      }
    }
  }
  return byCode;
}

/**
 * INTERSECTION du référentiel catalogue (hiérarchie complète familles/sous-familles) avec les profils du
 * PÉRIMÈTRE de la campagne (décision GATE 5). La structure d'arbre + recherche par niveau vient du catalogue
 * (le briefing n'a pas la sous-famille) ; on ne conserve que les profils du périmètre et on SURCHARGE leur
 * description par l'« attendu » du briefing quand il existe (sinon on garde la description catalogue).
 * Aucune mutation des entrées d'origine.
 */
export function buildPerimeterProfiles(
  allProfiles: readonly CatalogProfileDetailDto[],
  codes: ReadonlySet<string>,
  descriptionByCode: ReadonlyMap<string, string>,
): readonly CatalogProfileDetailDto[] {
  return allProfiles
    .filter((profile) => codes.has(profile.profileCode))
    .map((profile) => {
      const attendu = descriptionByCode.get(profile.profileCode);
      return attendu === undefined ? profile : { ...profile, description: attendu };
    });
}
